import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { createRunCommands } from './runCommands';
import { acceptsRunCommand, runCommandRefusal } from './runCommandContracts';
import { boundedOrderRead, useOrderWorkspaceSession } from './useOrderWorkspaceSession';
import { offlineRunPlan, staleRunPlan, withRunPlan } from '@/utils/offlineRunPlan';
import { runSendHold } from '@/utils/runSendRecovery';
import { getCachedRun, getCachedRuns, getOfflineDraftRun, getOfflineDraftRuns,
  getOrderReservationHold, getRun, getRuns, isOfflineDraftRun } from '@/api/runs';

// Coordinates one Dispatch screen with tab-lifetime command custody. It never
// stores a replay callback: review and refresh are observations only.
export default function useRunCommands({ store, date, driverMode, offline, refreshOrders }) {
  const session = useOrderWorkspaceSession();
  const latest = useRef(session); latest.current = session;
  const scope = JSON.stringify([session.key, date, driverMode, offline]);
  const latestScope = useRef(scope); latestScope.current = scope;
  const owner = useMemo(() => {
    const key = session.key;
    const controllers = new Set();
    const capture = local => {
      const captured = latestScope.current === scope && latest.current.key === key ? latest.current.capture(local) : null;
      if (!captured) return null;
      return { ...captured, config: { ...captured.config,
        ...(local ? { reviewLocalBase: captured.endpoint.replace(/\/api$/, '') } : {}) } };
    };
    const read = (execute, config) => boundedOrderRead(execute, config, controllers);
    return { capture, read, controllers, notices: new Set(), engine: createRunCommands(capture, read), active: false };
  }, [session.key, scope]);
  useLayoutEffect(() => {
    owner.active = true; owner.engine.activate();
    return () => {
      owner.active = false; owner.engine.retire();
      owner.controllers.forEach(controller => controller.abort());
      owner.notices.forEach(id => toast.dismiss(id)); owner.notices.clear();
    };
  }, [owner]);
  const pending = useSyncExternalStore(owner.engine.subscribe, owner.engine.getSnapshot);
  const sourceFor = runId => !offline ? 'cloud' : isOfflineDraftRun(store.getRun(runId)) ? 'draft' : 'cached';
  const specFor = spec => ({ date, source: spec.kind === 'create' && offline ? 'draft' : sourceFor(spec.runId), ...spec });
  const eligible = spec => !driverMode && store.isScopeCurrent() && offline === session.offline
    && (offline ? spec.source !== 'cloud' : spec.source === 'cloud');
  const reviewable = claim => claim?.date === date && (claim.source !== 'cloud') === offline;
  const observation = async (claim, captured, read) => {
    if (claim.kind === 'create') {
      const list = claim.source === 'cloud' ? getRuns : getOfflineDraftRuns;
      const rows = await read(config => list(claim.date, config), captured.config);
      if (!Array.isArray(rows) || rows.some(row => !row || typeof row.id !== 'string')) throw new Error('Invalid run list');
      if (!await refresh(claim, captured, 'list')) throw new Error('Date list refresh unavailable');
      const matches = rows.filter(row => row.name === claim.name).length;
      return { ready: true, duplicateRisk: true,
        message: `Current date list checked: ${matches} run(s) match the submitted name. This does not prove whether this request created a run; the list may be limited. Creating again may create a duplicate.` };
    }
    if (claim.kind === 'repair') {
      const hold = await read(config => getOrderReservationHold(claim.orderId, config), captured.config);
      if (!hold || typeof hold.can_clear !== 'boolean') throw new Error('Invalid reservation observation');
      return { ready: true, message: hold.can_clear
        ? 'Current reservation can be reviewed for repair. This does not establish the earlier repair outcome.'
        : 'Current reservation cannot be cleared. Review the order and its run before another action.' };
    }
    const detail = claim.source === 'cloud' ? getRun : claim.source === 'draft' ? getOfflineDraftRun : getCachedRun;
    try {
      let run = await read(config => detail(claim.runId, config), captured.config);
      if (!run || run.id !== claim.runId || typeof run.name !== 'string') throw new Error('Invalid run observation');
      // Publish a fresh owned detail observation, replacing any optimistic
      // name/stop order before permitting another deliberate command.
      if (!await refresh(claim, captured, 'all')) throw new Error('Run refresh unavailable');
      run = store.getRun(claim.runId);
      if (!run) throw new Error('Run disappeared during review');
      return { ready: true, duplicateRisk: claim.kind === 'notify',
        message: claim.kind === 'notify'
          ? 'Current run checked. Run state cannot confirm notification delivery. Sending again may notify the driver twice.'
          : `Current run: ${run.name}; status ${run.status || run.state || 'unknown'}. Review its current details before another action. This does not prove the earlier command outcome.` };
    } catch (error) {
      // Only the original target's explicit missing response is an observation;
      // network/authentication failures cannot become an absence claim.
      const expected = claim.source === 'cloud' ? 'Run not found'
        : claim.source === 'draft' ? 'Offline draft run not found' : 'Cached cloud run not found';
      if (error?.response?.status !== 404 || error.response.data?.detail !== expected) throw error;
      if (!await refresh(claim, captured, 'list')) throw new Error('Run list refresh unavailable');
      return { ready: true, duplicateRisk: claim.kind === 'notify',
        message: 'The original run is currently unavailable at its captured source. This does not prove which command caused that state.' };
    }
  };
  const refresh = async (spec, captured, mode = 'all', orders = false) => {
    const current = () => owner.active && captured.current() && store.isScopeCurrent();
    const read = (execute, config) => owner.read(async options => {
      if (!current()) throw new Error('Refresh retired');
      const result = await execute(options);
      if (!current()) throw new Error('Refresh retired');
      return result;
    }, config);
    if (!current()) return false;
    if (mode === 'list' || mode === 'all') {
      if (!await store.fetchRuns(captured.config, read)) return false;
    }
    if (!current()) return false;
    if ((mode === 'detail' || mode === 'all') && spec.runId && store.getRun(spec.runId)) {
      if (!await store.fetchRunDetails(spec.runId, captured.config, read)) return false;
    }
    if (!current()) return false;
    if (orders) {
      const result = await read(config => refreshOrders({ config, current, deliveryDate: spec.date }), captured.config);
      if (result?.state !== 'refreshed') return false;
    }
    return current();
  };
  return {
    async execute(input, { send, accept, message = 'Run change accepted', onStart, onAccepted,
      onRefused, refresh: mode = 'all', orders = false } = {}) {
      const spec = specFor(input);
      const plan = spec.source !== 'cloud' && spec.kind !== 'create'
        ? input.runPlan || offlineRunPlan(store.getRun(spec.runId)) : null;
      if (!eligible(spec) || owner.engine.blocked(spec) || (spec.source !== 'cloud' && runSendHold(store.getRun(spec.runId)))) return { accepted: false, state: 'blocked' };
      const captured = owner.capture(spec.source !== 'cloud');
      const current = () => owner.active && captured?.current() && store.isScopeCurrent();
      const selection = store.captureSelection?.();
      const feedbackCurrent = () => current() && (spec.kind === 'create' || !store.isSelectionCurrent || store.isSelectionCurrent(selection));
      let refreshed = false;
      let refusalError;
      let rawResponse;
      let receivedRawResponse = false;
      const result = await owner.engine.run(spec, {
        current,
        send: config => {
          onStart?.();
          const options = { ...config, reviewResponse: response => { receivedRawResponse = true; rawResponse = response.data; } };
          return send(plan ? withRunPlan(options, plan) : options);
        },
        accept: data => accept ? accept(data) : acceptsRunCommand(spec, receivedRawResponse ? rawResponse : data),
        refusal: error => {
          const reason = runCommandRefusal(spec, error);
          if (reason) refusalError = error;
          return reason;
        },
        message,
        onAccepted: async data => {
          await onAccepted?.(data);
          if (!current()) return;
          refreshed = await refresh(spec, captured, mode, orders);
          if (!refreshed) throw new Error('Accepted change; refresh unavailable');
        },
      });
      if (result.accepted && refreshed && current()) owner.engine.refreshed(result.id);
      if (current() && result.state !== 'retired') {
        if (result.state === 'refused') {
          const handled = onRefused?.(refusalError);
          if (staleRunPlan(refusalError)) {
            try { await refresh(spec, captured, 'all'); } catch { /* Refusal remains certain when observation fails. */ }
          }
          if (!handled && feedbackCurrent()) {
            const id = toast.error(result.message);
            if (id != null) owner.notices.add(id);
          }
        }
      }
      return result;
    },
    public: {
      scopeKey: scope,
      isCurrent: () => owner.active && store.isScopeCurrent() && Boolean(owner.capture(offline)?.current()),
      pending: pending.map(claim => ({ ...claim,
        canReview: reviewable(claim),
        ...(!reviewable(claim) ? { canAcknowledge: false, message: `Return to ${claim.date} with its original ${claim.source === 'cloud' ? 'online' : 'offline'} source to review this command.` } : {}),
        review: claim.review ? { message: claim.review } : null })),
      blockedRun: runId => {
        const spec = specFor({ kind: 'change', runId });
        return !eligible(spec) || owner.engine.blocked(spec) || (spec.source !== 'cloud' && !!runSendHold(store.getRun(runId)));
      },
      blockedOrder: orderId => owner.engine.blockedOrder(orderId, offline ? 'draft' : 'cloud'),
      blockedCreate: !eligible(specFor({ kind: 'create' })) || owner.engine.blocked(specFor({ kind: 'create' })),
      review: id => reviewable(owner.engine.getSnapshot().find(item => item.id === id)) && owner.engine.review(id, observation),
      acknowledge: (id, options) => reviewable(owner.engine.getSnapshot().find(item => item.id === id)) && owner.engine.acknowledge(id, options),
      async refresh(id) {
        const claim = owner.engine.getSnapshot().find(item => item.id === id);
        if (!claim || claim.state !== 'accepted') return false;
        if ((claim.source !== 'cloud') !== offline) return false;
        const captured = owner.capture(claim.source !== 'cloud');
        if (!captured) return false;
        try {
          // After navigation, observe the original date/source without filling
          // the replacement date's projection with that original run list.
          if (claim.date !== date || (claim.source !== 'cloud') !== offline) {
            const list = claim.source === 'cloud' ? getRuns : claim.source === 'draft' ? getOfflineDraftRuns : getCachedRuns;
            const rows = await owner.read(config => {
              if (!captured.current()) throw new Error('Refresh retired');
              return list(claim.date, config);
            }, captured.config);
            if (!Array.isArray(rows)) return false;
          } else if (!await refresh(claim, captured, 'all', true)) return false;
          return owner.active && captured.current() && owner.engine.refreshed(id);
        } catch { return false; }
      },
    },
  };
}
